import { useState, useEffect, useCallback, useRef } from 'react';

interface Testimonial {
  attribution: string;
  tag: 'Tenant' | 'Owner';
  quote: string;
}

const testimonials: Testimonial[] = [
  {
    attribution: "Mrs Namita & her son Aditya mentioned",
    tag: "Tenant",
    quote: "I was on the verge of going back home when they helped me choose an apartment after carefully understanding my requirement. They explained it until I fully understood it, and now I've finally moved in.",
  },
  {
    attribution: "Mr Ramji and his friend Vishal said",
    tag: "Owner",
    quote: "They really took effort guiding me on what fittings to add to my flat to place it on rent quicker, and parallelly got me wonderful tenants sooner than I expected.",
  },
  {
    attribution: "Mrs Vinita mentioned",
    tag: "Tenant",
    quote: "I was in a hurry to move into a 1BHK flat for my family and me. They understood my time crunch and eventually helped me pick one out. Their agreement process and shifting was also extremely quick.",
  },
  {
    attribution: "Mr Kadam said",
    tag: "Owner",
    quote: "I just got possession to my flat and wanted to rent it out. They guided me into getting the right furnishings and even inspected my flat for any defects or problems. I did not have to run around for anything.",
  },
  {
    attribution: "Mr Iyer said",
    tag: "Owner",
    quote: "I had a flat to rent out. The moment I told them this, they already had ready tenants. I was really satisfied by the people they got for me. Their process was extremely smooth and non-time consuming.",
  },
];

function TestimonialContent({ testimonial }: { testimonial: Testimonial }) {
  return (
    <>
      <p className="text-sm md:text-base text-foreground-800 mb-2 leading-relaxed">
        {testimonial.attribution}
      </p>

      <span
        className={`inline-block self-start text-[0.65rem] tracking-[0.15em] uppercase font-semibold px-3 py-0.5 rounded-full mb-4 md:mb-6 ${
          testimonial.tag === 'Tenant'
            ? 'bg-accent-100 text-accent-700'
            : 'bg-secondary-100 text-secondary-700'
        }`}
      >
        {testimonial.tag}
      </span>

      <blockquote className="text-[0.95rem] md:text-lg text-foreground-700 leading-relaxed md:leading-loose border-l-2 border-accent-300/60 pl-5 md:pl-6">
        &ldquo;{testimonial.quote}&rdquo;
      </blockquote>
    </>
  );
}

export default function TestimonialsSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback(
    (index: number) => {
      const len = testimonials.length;
      const normalized = ((index % len) + len) % len;
      setCurrentIndex(normalized);
    },
    [],
  );

  const goNext = useCallback(() => {
    goTo(currentIndex + 1);
  }, [currentIndex, goTo]);

  const goPrev = useCallback(() => {
    goTo(currentIndex - 1);
  }, [currentIndex, goTo]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        goNext();
      }, 7000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, goNext]);

  const testimonial = testimonials[currentIndex];

  return (
    <section className="relative w-full bg-background-50">
      <div className="w-full px-6 md:px-12 lg:px-16 py-12 md:py-20">
        {/* Section Label */}
        <p className="text-center text-[0.7rem] tracking-[0.3em] uppercase text-accent-500 mb-3 md:mb-4">
          TESTIMONIALS
        </p>

        {/* Main Heading */}
        <h2 className="text-center font-heading text-lg md:text-2xl lg:text-[1.625rem] leading-snug text-foreground-950 max-w-3xl mx-auto mb-2 md:mb-3">
          Trusted by tenants and owners across Navi Mumbai.
        </h2>

        {/* Supporting Line */}
        <p className="text-center text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-8 md:mb-14">
          Real experiences from people we've worked with. References can also be shared upon request.
        </p>

        {/* Testimonial Carousel */}
        <div
          className="relative max-w-2xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Card — all quotes are stacked in one grid cell so the card height always
              matches the tallest quote. The active one is visible; the rest reserve the
              space invisibly. This keeps the page rock-solid on auto-rotate (no reflow)
              while letting the card hug its content instead of being bloated. */}
          <div className="bg-background-50 border border-foreground-200/40 rounded-lg px-7 md:px-14 py-8 md:py-14 grid">
            {testimonials.map((t, index) => (
              <div
                key={t.attribution}
                className={`col-start-1 row-start-1 flex flex-col justify-center transition-opacity duration-500 ${
                  index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
              >
                <TestimonialContent testimonial={t} />
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-6 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50 border border-foreground-200/60 text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all duration-200 cursor-pointer"
            aria-label="Previous testimonial"
          >
            <i className="ri-arrow-left-s-line text-base md:text-lg" />
          </button>

          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-6 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50 border border-foreground-200/60 text-foreground-700 hover:text-foreground-950 hover:border-foreground-300 transition-all duration-200 cursor-pointer"
            aria-label="Next testimonial"
          >
            <i className="ri-arrow-right-s-line text-base md:text-lg" />
          </button>
        </div>

        {/* Navigation Dots */}
        <div className="relative z-10 flex items-center justify-center gap-2.5 mt-6 md:mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`w-2 h-2 flex-shrink-0 rounded-full transition-all duration-300 cursor-pointer ${
                index === currentIndex
                  ? 'bg-foreground-800 w-5'
                  : 'bg-foreground-300 hover:bg-foreground-500'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}